import { findCountryRegionTagByName, getCountryCodeForRegionTagValue } from '../facets/region-tag-labels';
import { normalizeFinlandLocationInput } from './is-finland-location';

const UNICODE_DASHES = /[\u2013\u2014\u2212\uFE58\uFE63\uFF0D]/g;

export type ProcessorLocationField = 'location_en' | 'location_sv' | 'location_fi';

/**
 * Canonical remote lines per locale (aligned with processor LOCATION MODEL).
 * Exported (TEC-482) so utils/geocoding/location-resolver.ts can build a reverse lookup
 * (canonical tail text -> key) instead of duplicating this table — the resolver's job is to
 * turn "Remote - EMEA" into a structured remoteEligibility scope, and this is the single
 * source of truth for which keys exist and what their canonical per-locale text is.
 */
export const REMOTE_LINE: Record<ProcessorLocationField, Record<string, string>> = {
  location_en: {
    GLOBAL: 'Remote - Global',
    EUROPE: 'Remote - Europe',
    EMEA: 'Remote - EMEA',
    APAC: 'Remote - APAC',
    MENA: 'Remote - MENA',
    EU: 'Remote - EU',
    NORDICS: 'Remote - Nordics',
    NORDIC: 'Remote - Nordic',
    ASIA: 'Remote - Asia',
    AFRICA: 'Remote - Africa',
    UK: 'Remote - United Kingdom',
    SCANDINAVIA: 'Remote - Scandinavia',
    BALTICS: 'Remote - Baltics',
    BENELUX: 'Remote - Benelux',
    NORTH_AMERICA: 'Remote - North America',
    SOUTH_AMERICA: 'Remote - South America',
    OCEANIA: 'Remote - Oceania',
    AMERICAS: 'Remote - Americas',
    LATAM: 'Remote - Latin America',
  },
  location_sv: {
    GLOBAL: 'Distans - Globalt',
    EUROPE: 'Distans - Europa',
    EMEA: 'Distans - EMEA',
    APAC: 'Distans - APAC',
    MENA: 'Distans - MENA',
    EU: 'Distans - EU',
    NORDICS: 'Distans - Norden',
    NORDIC: 'Distans - Norden',
    ASIA: 'Distans - Asien',
    AFRICA: 'Distans - Afrika',
    UK: 'Distans - Storbritannien',
    SCANDINAVIA: 'Distans - Skandinavien',
    BALTICS: 'Distans - Baltikum',
    BENELUX: 'Distans - Benelux',
    NORTH_AMERICA: 'Distans - Nordamerika',
    SOUTH_AMERICA: 'Distans - Sydamerika',
    OCEANIA: 'Distans - Oceanien',
    AMERICAS: 'Distans - Amerika',
    LATAM: 'Distans - Latinamerika',
  },
  location_fi: {
    GLOBAL: 'Etätyö - Globaali',
    EUROPE: 'Etätyö - Eurooppa',
    EMEA: 'Etätyö - EMEA',
    APAC: 'Etätyö - APAC',
    MENA: 'Etätyö - MENA',
    EU: 'Etätyö - EU',
    NORDICS: 'Etätyö - Pohjoismaat',
    NORDIC: 'Etätyö - Pohjoismaat',
    ASIA: 'Etätyö - Aasia',
    AFRICA: 'Etätyö - Afrikka',
    UK: 'Etätyö - Yhdistynyt kuningaskunta',
    SCANDINAVIA: 'Etätyö - Skandinavia',
    BALTICS: 'Etätyö - Baltia',
    BENELUX: 'Etätyö - Benelux',
    NORTH_AMERICA: 'Etätyö - Pohjois-Amerikka',
    SOUTH_AMERICA: 'Etätyö - Etelä-Amerikka',
    OCEANIA: 'Etätyö - Oseania',
    AMERICAS: 'Etätyö - Amerikat',
    LATAM: 'Etätyö - Latinalainen Amerikka',
  },
};

/** Remote/distans/etätyö word per locale — order matters: try the longer word first so
 * "etätyö" doesn't get pre-empted by a hypothetical shorter overlapping alternative. */
const REMOTE_WORD_BY_FIELD: Record<ProcessorLocationField, string[]> = {
  location_en: ['remote'],
  location_sv: ['distans', 'fjärr'],
  location_fi: ['etätyö', 'etä'],
};

/**
 * TEC-467 Bug 3: the LLM location extractor occasionally puts the remote-word first but with
 * the wrong (or no) separator before the scope/country — confirmed via manual review of live
 * data across all three locales: "Remote Canada" / "Remote, Poland" (missing/wrong separator),
 * "Etätyö (EMEA)" / "Distans (APAC)" (parenthesized tail). Deliberately does NOT handle the
 * *reversed* order ("Germany (Remote)", "Ranska, Etätyö") — real data includes short common
 * words that collide there ("Heltid, distans" = "Full-time, remote" in Swedish, not a place
 * called "Heltid"), so blindly swapping would sometimes produce wrong data. Only fires when the
 * remote-word is unambiguously first, which has no such collision risk.
 *
 * The tail must also start with an uppercase letter (a real place/region name or acronym
 * always does: "Canada", "EMEA", "Kanada"). Without this guard the pattern also matched
 * full-sentence descriptions that happen to start with the remote-word — confirmed via live
 * data: "Distans eller från ett av företagets kontor" ("Remote or from one of the company's
 * offices") and "Etätyö sallituista maista" ("Remote from permitted countries") both start
 * with a lowercase connector word, not a place, and must be left untouched rather than
 * reformatted into a confident-looking but still-non-specific "Remote - eller från ..." line.
 */
function tryFixRemoteWordFirstSeparator(
  normalized: string,
  field: ProcessorLocationField,
): string | undefined {
  const words = REMOTE_WORD_BY_FIELD[field].join('|');
  const tailLooksLikeAPlace = (tail: string) => /^[A-ZÅÄÖÜ0-9]/.test(tail);

  // "Remote (Tail)" / "Etätyö (Tail)" — remote-word first, parenthesized tail.
  const paren = normalized.match(new RegExp(`^\\s*(${words})\\s*\\(\\s*(.+?)\\s*\\)\\s*$`, 'i'));
  if (paren && tailLooksLikeAPlace(paren[2])) return `${paren[1]} - ${paren[2].trim()}`;

  // "Remote Tail" (no separator) / "Remote, Tail" (comma) — remote-word first, real tail
  // follows. Excludes strings already using " - " (tail would start with "-").
  const spaceOrComma = normalized.match(new RegExp(`^\\s*(${words})\\s*,?\\s+(\\S.*)$`, 'i'));
  if (spaceOrComma && !/^-/.test(spaceOrComma[2]) && tailLooksLikeAPlace(spaceOrComma[2])) {
    return `${spaceOrComma[1]} - ${spaceOrComma[2].trim()}`;
  }

  return undefined;
}

/**
 * TEC-467 Bug 3: a handful of entries are just the bare word for "remote work" with no scope
 * at all ("Etätyöskentely", "Etätyömahdollisuus", "Distansarbete") or the literal untranslated
 * "City, Country"-style prompt placeholder the model sometimes echoes back unfilled. Neither
 * carries any location information beyond what the separate `remoteness` field already captures
 * — same reasoning as the "Remote - Home based" drop below, generalized to these confirmed-live
 * cases across all three locales.
 */
const DROP_EXACT_MATCHES = new Set([
  'etätyöskentely',
  'etätyömahdollisuus',
  'distansarbete',
  'city, country',
  'kaupunki, maa',
  'stad, land',
]);

/**
 * TEC-482 follow-up: a location entry that is *only* the bare remote-word, with no scope tail
 * at all ("Remote", "Anywhere", "Etätyö") — as opposed to the DROP_EXACT_MATCHES nouns above
 * ("Etätyöskentely" etc.) or the "<region> - home based" case below, both of which describe a
 * work *mode* alongside a real location stated elsewhere and carry no geographic claim of their
 * own. Confirmed live: every sampled job whose location_en was exactly "Remote" or "Anywhere"
 * had it as the *only* location entry, not a secondary note next to a city — i.e. it's the
 * posting's entire location signal, meaning "open to remote candidates anywhere," the same claim
 * an explicit "Remote - Worldwide" line already makes. Canonicalizing it to the field's GLOBAL
 * remote line lets resolveJobLocationEntry's existing global-scope path
 * (computeRegionTagsForResolutions: every recruiting region + continent code) pick it up,
 * instead of falling through to resolvePhysicalPlace and failing to match "Remote" as a city
 * name — which previously left these jobs with empty regionTags entirely.
 *
 * Each locale's own translated synonyms ("Maailmanlaajuinen"/"Missä tahansa"/"Globaali" fi,
 * "Globalt"/"Världsomspännande"/"Världen över"/"Var som helst" sv) are confirmed-live values,
 * not guessed translations — pulled from real location_fi/location_sv entries paired with their
 * English location_en sibling on the same job (e.g. a "Global" en / "Globaali" fi / "Globalt" sv
 * triple from the same Marriott International posting). The English words are ALSO checked
 * against every field, not just location_en: confirmed live that the LLM sometimes leaves the
 * English word untranslated in location_sv (a job with location_en: "Worldwide" whose
 * location_sv sibling is also literally "Worldwide"/"World Wide", not a Swedish translation) —
 * same cross-field English-artifact pattern INVERTED_REGION_TO_KEY/HOME_BASED_TAIL_TO_KEY below
 * already handle for the "<region> - home based" case (those dictionaries are English-keyed and
 * matched regardless of which field they're applied to).
 */
const ENGLISH_BARE_GLOBAL_WORDS = new Set(['remote', 'anywhere', 'worldwide', 'world wide', 'global', 'world']);
const BARE_REMOTE_WORD_TO_GLOBAL: Record<ProcessorLocationField, Set<string>> = {
  location_en: ENGLISH_BARE_GLOBAL_WORDS,
  location_fi: new Set([
    'etätyö',
    'etä',
    'maailmanlaajuinen',
    'missä tahansa',
    'globaali',
    ...ENGLISH_BARE_GLOBAL_WORDS,
  ]),
  location_sv: new Set([
    'distans',
    'fjärr',
    'globalt',
    'världsomspännande',
    'världen över',
    'var som helst',
    ...ENGLISH_BARE_GLOBAL_WORDS,
  ]),
};

/** Left-hand side of "<region> - home based" → canonical key. */
const INVERTED_REGION_TO_KEY: Record<string, string> = {
  emea: 'EMEA',
  apac: 'APAC',
  mena: 'MENA',
  eu: 'EU',
  europe: 'EUROPE',
  nordics: 'NORDICS',
  nordic: 'NORDIC',
  asia: 'ASIA',
  africa: 'AFRICA',
  uk: 'UK',
  'united kingdom': 'UK',
  scandinavia: 'SCANDINAVIA',
  skandinavien: 'SCANDINAVIA',
  baltics: 'BALTICS',
  baltic: 'BALTICS',
  benelux: 'BENELUX',
  'north america': 'NORTH_AMERICA',
  'south america': 'SOUTH_AMERICA',
  oceania: 'OCEANIA',
  americas: 'AMERICAS',
  latam: 'LATAM',
  'latin america': 'LATAM',
  worldwide: 'GLOBAL',
  global: 'GLOBAL',
  world: 'GLOBAL',
};

/** Tail of "home-based - <tail>" → canonical key (worldwide → GLOBAL). */
const HOME_BASED_TAIL_TO_KEY: Record<string, string> = {
  worldwide: 'GLOBAL',
  'world-wide': 'GLOBAL',
  global: 'GLOBAL',
  world: 'GLOBAL',
  europe: 'EUROPE',
  emea: 'EMEA',
  apac: 'APAC',
  mena: 'MENA',
  eu: 'EU',
  nordics: 'NORDICS',
  nordic: 'NORDIC',
  asia: 'ASIA',
  africa: 'AFRICA',
  uk: 'UK',
  'united kingdom': 'UK',
  scandinavia: 'SCANDINAVIA',
  baltics: 'BALTICS',
  benelux: 'BENELUX',
  'north america': 'NORTH_AMERICA',
  'south america': 'SOUTH_AMERICA',
  oceania: 'OCEANIA',
  americas: 'AMERICAS',
  latam: 'LATAM',
  'latin america': 'LATAM',
  anywhere: 'GLOBAL',
};

function scopeTailPrimary(tail: string): string {
  return tail.split(/[,;(]/)[0].trim().replace(/\s+/g, ' ').toLowerCase();
}

function tryFixInvertedRegionHomeBased(
  normalized: string,
  field: ProcessorLocationField,
): string | undefined {
  const m = normalized.match(/^\s*([a-zåäöü][a-zåäöü0-9\s]{0,48}?)\s*-\s*home\s*-?\s*base?d?\s*$/i);
  if (!m) return undefined;
  const raw = m[1].trim().replace(/\s+/g, ' ').toLowerCase();
  const key = INVERTED_REGION_TO_KEY[raw];
  if (!key) return undefined;
  const line = REMOTE_LINE[field][key];
  return line ?? undefined;
}

function tryFixHomeBasedFirst(
  normalized: string,
  field: ProcessorLocationField,
): string | undefined {
  const m = normalized.match(/^\s*home\s*-?\s*based\s*-\s*(.+)$/i);
  if (!m) return undefined;
  const primary = scopeTailPrimary(m[1].trim());
  if (!primary) return undefined;
  const key = HOME_BASED_TAIL_TO_KEY[primary];
  if (!key) return undefined;
  const line = REMOTE_LINE[field][key];
  return line ?? undefined;
}

/**
 * "<remote-word> - <tail>" where the tail is itself a global synonym, not a real region — e.g.
 * "Remote - Worldwide", "Remote - Anywhere", "Etätyö - Maailmanlaajuinen". Confirmed live
 * (2026-09-12): "Remote - Worldwide" alone had 177+ occurrences in the UnmatchableLocations
 * queue, unresolved because REMOTE_LINE's own canonical GLOBAL text is "Remote - Global", not
 * "Remote - Worldwide" — the tail never matched via the exact-string reverse lookup in
 * location-resolver.ts, and it isn't a gazetteer country either. Reuses the same synonym set
 * BARE_REMOTE_WORD_TO_GLOBAL already established for the standalone-word case ("Remote" alone),
 * just applied to the tail of an already remote-word-prefixed line instead of the whole string.
 */
function tryFixGlobalSynonymTail(normalized: string, field: ProcessorLocationField): string | undefined {
  const words = REMOTE_WORD_BY_FIELD[field].join('|');
  const m = normalized.match(new RegExp(`^\\s*(?:${words})\\s*-\\s*(.+)$`, 'i'));
  if (!m) return undefined;
  const tail = m[1].trim().replace(/\s+/g, ' ').toLowerCase();
  if (!BARE_REMOTE_WORD_TO_GLOBAL[field].has(tail)) return undefined;
  return REMOTE_LINE[field].GLOBAL;
}

/**
 * Fixes the LLM occasionally truncating "Etätyö - <scope>" to "Etä - <scope>"
 * (dropping "työ"), for any scope tail — not just the canonical region enum —
 * since production data includes country tails (e.g. "Etä - Yhdysvallat") and
 * combined lists (e.g. "Etä - APAC, EMEA") that never had a REMOTE_LINE entry
 * to look up. Only applies to location_fi — "Remote"/"Distans" have no shorter
 * valid prefix that collides the same way.
 */
function tryFixTruncatedEtaPrefix(
  normalized: string,
  field: ProcessorLocationField,
): string | undefined {
  if (field !== 'location_fi') return undefined;
  const m = normalized.match(/^\s*etä\s*-\s*(.+)$/i);
  if (!m) return undefined;
  const tail = m[1].trim().replace(/\s+/g, ' ');
  if (!tail) return undefined;
  return `Etätyö - ${tail}`;
}

/**
 * Countries whose capital shares the country's name ("Luxembourg, Luxembourg" is a real city),
 * so a repeated name is not evidence of a filler city there. Matched by country placeId, so every
 * locale spelling ("Luxemburg", "Singapur") is covered.
 */
const CITY_STATE_COUNTRY_NAMES = ['Luxembourg', 'Singapore', 'Monaco', 'Kuwait', 'Panama', 'Guatemala', 'Djibouti', 'San Marino', 'Vatican', 'Mexico'];
let cityStateCountryIds: Set<string> | null = null;
function isCityStateCountry(placeId: string): boolean {
  cityStateCountryIds ??= new Set(CITY_STATE_COUNTRY_NAMES.map((n) => findCountryRegionTagByName(n)).filter((id): id is string => !!id));
  return cityStateCountryIds.has(placeId);
}

/**
 * Real cities whose name is also a country's name in another locale: "Brasilia" is Finnish for
 * Brazil, so "Brasilia, Brasilien" (a Softswiss role in Brasília, 2026-10-05) must stay a city.
 * Accent-insensitive.
 */
const CITY_NAMES_THAT_ARE_COUNTRY_NAMES = new Set(['brasilia']);

/** Codes the extractor uses that differ from the ISO 3166-1 alpha-2 code. */
const COUNTRY_CODE_ALIASES: Record<string, string> = { UK: 'GB' };

/**
 * A country-only location the extractor forced into the "City, Country" shape by filling the city
 * slot with the country itself or its code. Confirmed live (2026-10-04): a Lever posting for
 * "Norway, Sweden, Finland" came out as location_fi "Norja, Norja" / "Suomi, Suomi", and an
 * Eightfold posting with country codes came out as "Be, Belgium", "Nl, Netherlands",
 * "Finland, Finland". Collapses to the bare country (in the field's own wording), which the
 * location resolver already maps to the country place. Leaves a real city alone, including a
 * city-state capital that shares its country's name.
 */
function tryCollapseCountryOnlyPair(normalized: string): string | undefined {
  const segments = normalized.split(',').map((s) => s.trim());
  if (segments.length !== 2 || !segments[0] || !segments[1]) return undefined;
  const [head, country] = segments;
  const countryId = findCountryRegionTagByName(country);
  if (!countryId) return undefined;
  if (CITY_NAMES_THAT_ARE_COUNTRY_NAMES.has(head.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase())) return undefined;
  if (findCountryRegionTagByName(head) === countryId || head.toLowerCase() === country.toLowerCase()) {
    return isCityStateCountry(countryId) ? undefined : country;
  }
  if (/^[a-z]{2}$/i.test(head)) {
    const code = head.toUpperCase();
    if ((COUNTRY_CODE_ALIASES[code] ?? code) === getCountryCodeForRegionTagValue(countryId)) return country;
  }
  return undefined;
}

/**
 * The LLM occasionally grafts a work-mode descriptor (Hybrid/Onsite/On-site/"work from
 * home"/"fully remote") directly onto an otherwise-real location string, in violation of the
 * processor's own LOCATION MODEL rule that remoteness belongs only in remoteness_*, never in
 * location_*. Confirmed via a full scan of the live production UnmatchableLocations review
 * queue (2026-09-12, 12,452 rows): "Frankfurt am Main - Hybrid" (99 occurrences), "Berlin
 * (Hybrid)" (94), "Hybrid - Helsinki, Uusimaa, Finland" (88), "Espoo Hybrid" (34), "United
 * States - Onsite" (17), "Onsite, United States" (20), "Florida, USA (work from home)" (51),
 * "Germany (Remote/Hybrid/On-site)" (31). The underlying place name is real and worth keeping ,
 * only the work-mode word is noise (remoteness_* already captures it), so this strips the
 * descriptor rather than dropping the whole entry, unlike the bare-word-with-no-scope handling
 * elsewhere in this file.
 *
 * Deliberately conservative, same reasoning as the rest of this file (a false positive here
 * CORRUPTS good data, worse than missing an occasional dirty value): only fires when what
 * remains after stripping still looks like a real place (starts with an uppercase letter and is
 * not schedule or travel commentary) and is not itself another bare work-mode/remote word, "Remote - Hybrid" must NOT
 * reduce to "Remote", since that has no place information either (bare "Remote" is handled, if
 * at all, by the global-remote-word path elsewhere in this function, not here). Free-text tails
 * that clearly aren't a place ("on-site + remote", "in-office Tue-Thu; remote Mon & Fri") are
 * excluded by the same uppercase-start guard, and phrases only match at the very start or end of
 * the string (never mid-string), so "Koti tai Hybrid, Kalifornia" (the work-mode word embedded
 * in the middle of a sentence) is correctly left untouched rather than guessed at.
 */
const WORK_MODE_PHRASES_BY_FIELD: Record<ProcessorLocationField, string[]> = {
  // Longest/most-specific phrases first so a shorter phrase can't pre-empt a longer match.
  location_en: ['work from home', 'fully remote', 'on-site', 'on site', 'onsite', 'hybrid'],
  // The extractor often writes the English word even in the fi/sv fields ("Hybrid - Suomi",
  // "Onsite, Nordics / Europe"; confirmed live 2026-10-14), so those fields accept it too.
  location_fi: ['hybridityö', 'hybridi', 'hybrid', 'on-site', 'on site', 'onsite'],
  location_sv: ['på plats', 'hybrid', 'on-site', 'on site', 'onsite'],
};

function escapeRegExpLiteral(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Free-text schedule or travel commentary that is not a place even though it starts with a capital
 * letter: "3 days in office / 2 days work from home" (leftover of "Hybrid: 3 days in office ..."),
 * "Denmark travel". Such a remainder is left for review instead of being written as a location.
 */
const SCHEDULE_OR_TRAVEL_WORDS = /\b(days?|päivää|päivä|dagar|dag|travel|matkustus|resor|week|viikko|vecka)\b/i;

const WORK_MODE_OR_REMOTE_WORD = /\b(remote|hybrid|hybridi|onsite|on-site|etätyö|etätyöt|distans|distansarbete)\b/i;

function looksLikePlaceStart(candidate: string): boolean {
  const t = candidate.trim();
  // Starts with a capital letter, not a digit: a remainder like "3 days in office" is a schedule.
  return /^[A-ZÅÄÖÜ]/.test(t) && !SCHEDULE_OR_TRAVEL_WORDS.test(t);
}

function unwrapFullyParenthesized(s: string): string {
  const m = s.trim().match(/^\(\s*(.+?)\s*\)$/);
  return m ? m[1] : s;
}

function isUselessAsLocation(candidate: string, field: ProcessorLocationField): boolean {
  const t = candidate.trim().toLowerCase();
  if (!t) return true;
  if (WORK_MODE_PHRASES_BY_FIELD[field].includes(t)) return true;
  if (BARE_REMOTE_WORD_TO_GLOBAL[field].has(t)) return true;
  return false;
}

/**
 * Mirrors the standalone-country-name blocklist in processor.ts's cleanInvalidLocations (which
 * drops exactly these values from freshly-extracted location arrays), kept here so a stripped
 * work-mode remainder is held to the same shape rule, not a looser one. A bare country with no
 * city/remote-scope is genuinely ambiguous per the LOCATION MODEL (ad-hoc "Onsite - Finland" /
 * "Hybrid - Poland" give no city, and the job isn't remote, so neither "City, Country" nor
 * "Remote - Country" applies), better to leave the original string untouched (still queued for
 * manual review / reprocess) than to write a shape the live pipeline would itself reject.
 */
const BARE_COUNTRY_NAMES = new Set([
  'finland', 'united states', 'usa', 'sweden', 'norway', 'denmark', 'germany', 'france', 'spain', 'italy', 'poland',
  'suomi', 'yhdysvallat', 'ruotsi', 'norja', 'tanska', 'saksa', 'ranska', 'espanja', 'italia', 'puola',
  'sverige', 'norge', 'danmark', 'tyskland', 'frankrike', 'spanien', 'italien', 'polen',
]);

/**
 * A stripped candidate is only accepted if it's a clean place-shaped remainder, not a bare
 * work-mode word (checked above by isUselessAsLocation), not a bare country name on its own
 * (see BARE_COUNTRY_NAMES), and not a fragment with a dangling separator left over from a
 * punctuated form the bare-space branches shouldn't have touched in the first place. Without
 * that last check, "Remote - Hybrid" (already punctuated, and correctly rejected by the
 * suffixPunct branch above since its head "Remote" is itself a bare remote word) would still get
 * re-matched by the bare-space suffix branch, whose looser boundary treats the space inside
 * "Remote -[space]Hybrid" as if it were a real word gap, wrongly returning "Remote -".
 */
function isCleanPlaceRemainder(candidate: string, field: ProcessorLocationField): boolean {
  if (!looksLikePlaceStart(candidate)) return false;
  if (isUselessAsLocation(candidate, field)) return false;
  if (BARE_COUNTRY_NAMES.has(candidate.trim().toLowerCase())) return false;
  if (/[-:,&+/]\s*$/.test(candidate) || /^\s*[-:,&+/]/.test(candidate)) return false;
  // Another work-mode or remote word still inside the remainder ("Remote & Onsite") means this is
  // a list of work arrangements, not a place.
  if (WORK_MODE_OR_REMOTE_WORD.test(candidate)) return false;
  return true;
}

function tryStripWorkModeContamination(
  normalized: string,
  field: ProcessorLocationField,
): string | undefined {
  const phrases = WORK_MODE_PHRASES_BY_FIELD[field];

  // A parenthetical tail made up ENTIRELY of work-mode/remote words joined by "/", e.g.
  // "Germany (Remote/Hybrid/On-site)", is unambiguous remoteness commentary no matter how many
  // modes are listed, so strip the whole parenthetical rather than requiring one exact phrase.
  const parenAny = normalized.match(/^(.+?)\s*\(([^()]+)\)\s*$/);
  if (parenAny) {
    const head = parenAny[1].trim();
    const parts = parenAny[2].split('/').map((p) => p.trim().toLowerCase());
    const allWorkMode =
      parts.length > 1 && parts.every((p) => phrases.includes(p) || BARE_REMOTE_WORD_TO_GLOBAL[field].has(p));
    if (allWorkMode && isCleanPlaceRemainder(head, field)) {
      return head;
    }
  }

  for (const phrase of phrases) {
    const esc = escapeRegExpLiteral(phrase);

    // Prefix forms: "Hybrid - Tail", "Hybrid: Tail", "Hybrid, Tail", "Hybrid from Tail".
    const prefix = normalized.match(new RegExp(`^${esc}\\s*(?:[-:,]\\s*|from\\s+)(.+)$`, 'i'));
    if (prefix) {
      const tail = unwrapFullyParenthesized(prefix[1].trim());
      if (isCleanPlaceRemainder(tail, field)) return tail;
    }

    // Suffix forms: "Tail - Hybrid", "Tail, Hybrid", "Tail (Hybrid)".
    const suffixPunct = normalized.match(new RegExp(`^(.+?)\\s*[-,]\\s*${esc}$`, 'i'));
    if (suffixPunct) {
      const head = suffixPunct[1].trim();
      if (isCleanPlaceRemainder(head, field)) return head;
    }
    const suffixParen = normalized.match(new RegExp(`^(.+?)\\s*\\(\\s*${esc}\\s*\\)$`, 'i'));
    if (suffixParen) {
      const head = suffixParen[1].trim();
      if (isCleanPlaceRemainder(head, field)) return head;
    }

    // Bare-space forms (no punctuation): "Hybrid Tail" / "Tail Hybrid", restricted to
    // single-word phrases to avoid ambiguous splits on a multi-word phrase like "work from home".
    if (!phrase.includes(' ')) {
      const prefixSpace = normalized.match(new RegExp(`^${esc}\\s+(.+)$`, 'i'));
      if (prefixSpace) {
        const tail = unwrapFullyParenthesized(prefixSpace[1].trim());
        if (isCleanPlaceRemainder(tail, field)) return tail;
      }
      const suffixSpace = normalized.match(new RegExp(`^(.+?)\\s+${esc}$`, 'i'));
      if (suffixSpace) {
        const head = suffixSpace[1].trim();
        if (isCleanPlaceRemainder(head, field)) return head;
      }
    }
  }

  return undefined;
}

/**
 * Fixes or drops malformed LLM location lines (wrong ordering: region/work mode before scope).
 * Returns null when the entry must be removed.
 */
export function sanitizeProcessorLocationEntry(
  loc: string,
  field: ProcessorLocationField,
): string | null {
  if (typeof loc !== 'string') return null;
  const trimmed = loc.trim();
  if (!trimmed) return null;

  const normalized = normalizeFinlandLocationInput(trimmed.replace(UNICODE_DASHES, '-'));

  const fixedInverted = tryFixInvertedRegionHomeBased(normalized, field);
  if (fixedInverted !== undefined) return fixedInverted;

  const fixedHomeFirst = tryFixHomeBasedFirst(normalized, field);
  if (fixedHomeFirst !== undefined) return fixedHomeFirst;

  const fixedTruncatedEta = tryFixTruncatedEtaPrefix(normalized, field);
  if (fixedTruncatedEta !== undefined) return fixedTruncatedEta;

  const lower = normalized.toLowerCase();

  // TEC-467 Bug 3: bare "remote work" word with no scope, or the untranslated prompt
  // placeholder — neither carries location information; drop (see DROP_EXACT_MATCHES docstring).
  if (DROP_EXACT_MATCHES.has(lower)) {
    return null;
  }

  // TEC-482 follow-up: bare remote-word with no scope qualifier at all ("Remote", "Anywhere",
  // "Etätyö") — canonicalize to the field's global remote line so resolveJobLocationEntry treats
  // it as globally remote-eligible instead of failing to match it as a physical place.
  if (BARE_REMOTE_WORD_TO_GLOBAL[field].has(lower)) {
    return REMOTE_LINE[field].GLOBAL;
  }

  const fixedWorkModeContamination = tryStripWorkModeContamination(normalized, field);
  if (fixedWorkModeContamination !== undefined) return fixedWorkModeContamination;

  const fixedRemoteWordFirstSeparator = tryFixRemoteWordFirstSeparator(normalized, field);
  if (fixedRemoteWordFirstSeparator !== undefined) return fixedRemoteWordFirstSeparator;

  const fixedGlobalSynonymTail = tryFixGlobalSynonymTail(normalized, field);
  if (fixedGlobalSynonymTail !== undefined) return fixedGlobalSynonymTail;

  // "Remote - Home based" — work mode only; drop (remoteness enum carries it).
  if (/^(remote|distans|etätyö|etä|fjärr)\s*-\s*home\s*-?\s*base?d?\s*$/i.test(normalized)) {
    return null;
  }

  // Remaining "<something> - home based" without valid remote prefix → drop
  if (
    /\bhome\s*-?\s*base?d?\b/i.test(lower) &&
    !/^(remote|distans|etätyö|etä|fjärr)\s*-\s*/i.test(normalized)
  ) {
    return null;
  }

  // Unfixed "home-based - …" (unrecognised tail already handled above)
  if (/^home\s*-?\s*based\s*-/i.test(lower)) {
    return null;
  }

  const collapsedCountry = tryCollapseCountryOnlyPair(normalized);
  if (collapsedCountry !== undefined) return collapsedCountry;

  // TEC-467 Bug 3: return the dash-normalized form, not the raw original — otherwise the
  // Unicode-dash normalization computed into `normalized` above (used for every pattern match
  // in this function) was silently discarded whenever no other special case fired, leaving
  // e.g. "Etätyö – Suomi" (en dash) unfixed even though every check above treated it as if it
  // already used a plain hyphen.
  return normalized;
}
